import {
  Component,
  h,
  Element,
  Prop,
  Event,
  EventEmitter,
  Watch,
} from "@stencil/core"
import { animation, translate } from "../../core/helpers/webcomponent-helpers"

@Component({
  tag: "sdx-filter",
  styleUrl: "filter.scss",
  shadow: true,
})
export class Filter {
  private chips: Map<string, HTMLSdxButtonElement> = new Map()
  private selectedOptionsRemoved: string[] = []
  private selectedOptionsAdded: string[] = []
  private scrollViewEl?: HTMLSdxScrollViewElement

  @Element() public el!: HTMLSdxFilterElement

  /**
   * All the selected filter options
   */
  @Prop() public selection: string[] = []

  /**
   * The text of the "show more..." element
   */
  @Prop() public showMoreLabel: string = translate(
    "Show more filters",
    this.el.lang
  )

  /**
   * The text of the "show less..." element
   */
  @Prop() public showLessLabel: string = translate(
    "Show less filters",
    this.el.lang
  )

  /**
   * The text of the "Reset all filters" chip
   */
  @Prop() public resetAllFiltersLabel: string = translate(
    "Reset all filters",
    this.el.lang
  )

  /**
   * The text of the result label
   */
  @Prop() public resultLabel: string = ""

  /**
   * Disable animations for testing.
   * @private
   */
  @Prop() public animated: boolean = true

  /**
   * Emitted when user clicks on a chip
   */
  @Event() public sdxdeselectrequest!: EventEmitter<{ label: string }>

  @Watch("selection") public onSelectedOptionsChanged(
    newSelection: string[],
    oldSelection: string[]
  ) {
    // mark options to show remove animation in componentWillUpdate
    oldSelection.forEach((oldOption) => {
      if (!newSelection.includes(oldOption)) {
        this.selectedOptionsRemoved.push(oldOption)
      }
    })

    if (!newSelection.length) {
      this.selectedOptionsRemoved.push(this.resetAllFiltersLabel)
    }

    // mark options to show add animation in componentDidUpdate
    newSelection.forEach((newOption) => {
      if (!oldSelection.includes(newOption)) {
        this.selectedOptionsAdded.push(newOption)
      }
    })
  }

  public async componentWillUpdate() {
    await Promise.all(
      this.selectedOptionsRemoved.map((option) => {
        const animationEl = this.chips.get(option)
        this.chips.delete(option)

        return (
          animationEl &&
          animation.scaleOut(animationEl, this.animated ? undefined : 0)
        )
      })
    )

    this.selectedOptionsRemoved = []
  }

  public async componentDidUpdate() {
    await Promise.all(
      this.selectedOptionsAdded.map((option) => {
        const animationEl = this.chips.get(option)

        return (
          animationEl &&
          animation.scaleIn(animationEl, this.animated ? undefined : 0)
        )
      })
    )

    this.selectedOptionsAdded = []
    this.scrollViewEl?.layout()
  }

  private createChipEl(label: string) {
    return (
      <sdx-button
        ref={(el) => this.chips.set(label, el!)}
        key={label}
        theme="chip"
        class="chip"
        label={label}
        onClick={(e) => {
          this.sdxdeselectrequest.emit({
            label: (e.target as HTMLSdxButtonElement).label,
          })
        }}
      />
    )
  }

  private getScrollViewWrapperClassNames() {
    return {
      "scroll-view-wrapper": true,
      space: this.selection.length > 0,
    }
  }

  public render() {
    return (
      <div class="component">
        <slot />

        {this.el.querySelectorAll("[slot=filter-more]").length > 0 && (
          <sdx-expand-and-collapse
            arrow-position="fixed"
            expand-label={this.showMoreLabel}
            collapse-label={this.showLessLabel}
          >
            <slot name="filter-more" />
          </sdx-expand-and-collapse>
        )}

        <div class={this.getScrollViewWrapperClassNames()}>
          <sdx-scroll-view ref={(el) => (this.scrollViewEl = el)}>
            <div class="chips-wrapper">
              {this.selection.length > 0 &&
                this.createChipEl(this.resetAllFiltersLabel)}
              {this.selection.map((option) => this.createChipEl(option))}
            </div>
          </sdx-scroll-view>
        </div>

        {this.resultLabel && <span class="result">{this.resultLabel}</span>}
      </div>
    )
  }
}
